import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import Table from '@antimatterhq/ui/components/table';
import { QueryResultsTableSkeleton } from '@/pages/explore/query/_partials/queryResultsTable/QueryResultTableSkeletons';
import { QueryResultsVirtualizedTableContent } from '@/pages/explore/query/_partials/queryResultsTable/QueryResultsVirtualizedTableContent';
import { QueryResultsTableData } from '@/pages/explore/query/_partials/queryResultsTable/QueryResultsTableData';
import {
  activeQueryTabIdAtom,
  queryTabAtomFamily,
  updateQueryResultsDrawerStateAtomFamily,
} from '@/atoms/query/queryAtoms';
import { useQueryResultsColumnDefinitions } from '@/hooks/components/query/useQueryResultsColumnDefinitions';
import { useRunQuery } from '@/hooks/components/query/useRunQuery';

export function QueryResultsTable() {
  const activeTabId = useAtomValue(activeQueryTabIdAtom);

  const tab = useAtomValue(queryTabAtomFamily(activeTabId ?? ''));
  const updateDrawerState = useSetAtom(
    updateQueryResultsDrawerStateAtomFamily(activeTabId ?? ''),
  );
  const { isPending: isRunningQuery } = useRunQuery(activeTabId ?? '');
  const columnDefinitions = useQueryResultsColumnDefinitions(activeTabId ?? '');

  const handleRowClick = useCallback(
    (row: QueryResultsTableData) => {
      updateDrawerState({
        isOpen: true,
        isExpanded: false,
        selectedRowData: row,
      });
    },
    [updateDrawerState],
  );

  // Use queryId if available, otherwise fall back to tabId
  const localStorageKey = tab?.queryId
    ? `query-results-table-${tab.queryId}`
    : `query-results-table-${activeTabId ?? ''}`;

  if (isRunningQuery || !columnDefinitions.length) {
    return <QueryResultsTableSkeleton />;
  }

  return (
    <Table.Provider
      columnDefinitions={columnDefinitions}
      localStorageKeyPrefix={localStorageKey}
    >
      <QueryResultsVirtualizedTableContent
        isRunningQuery={isRunningQuery}
        onRowClick={handleRowClick}
      />
    </Table.Provider>
  );
}
